import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { getBousalaAnalytics } from "@/lib/bousala/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const analyticsQ = () =>
  queryOptions({ queryKey: ["admin", "bousala", "analytics"], queryFn: () => getBousalaAnalytics() });

export const Route = createFileRoute("/_authenticated/admin/bousala/analytics")({
  loader: ({ context }) => context.queryClient.ensureQueryData(analyticsQ()),
  head: () => ({
    meta: [{ title: "تحليلات بوصلة د. سارة" }, { name: "robots", content: "noindex" }],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: AnalyticsPage,
});

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function AnalyticsPage() {
  const { data } = useSuspenseQuery(analyticsQ());
  return (
    <div className="space-y-6" dir="rtl">
      <header>
        <p className="kicker text-modern-orange">بوصلة د. سارة</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold">التحليلات</h1>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-lg">قمع المحاولات — كل الوقت</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="بدأوا" value={data.funnel_all.started} />
            <Row label="أكملوا" value={data.funnel_all.completed} />
            <Row label="التقرير جاهز" value={data.funnel_all.report_ready} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">آخر 30 يوماً</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="بدأوا" value={data.funnel_30.started} />
            <Row label="أكملوا" value={data.funnel_30.completed} />
            <Row label="التقرير جاهز" value={data.funnel_30.report_ready} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">تحويل الفئات — كل الوقت</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="مجاني" value={data.tiers_all.free} />
            <Row label="كامل" value={data.tiers_all.full} />
            <Row label="بريميوم" value={data.tiers_all.premium} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">تحويل الفئات — 30 يوماً</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="مجاني" value={data.tiers_30.free} />
            <Row label="كامل" value={data.tiers_30.full} />
            <Row label="بريميوم" value={data.tiers_30.premium} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">الإيرادات حسب الفئة (USD)</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="كامل" value={money(data.revenue_cents_by_tier.full ?? 0)} />
            <Row label="بريميوم" value={money(data.revenue_cents_by_tier.premium ?? 0)} />
            <Row label="غير مصنّف" value={money(data.revenue_cents_by_tier.other ?? 0)} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">الإيرادات حسب البيئة</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="اختبار" value={money(data.revenue_cents_by_env.sandbox ?? 0)} />
            <Row label="حقيقي" value={money(data.revenue_cents_by_env.live ?? 0)} />
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-lg">جلسات التفسير</CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{data.interpretation_sessions}</p>
            <p className="mt-1 text-xs text-muted-foreground">حجوزات جلسة تفسير بوصلة (كل الوقت، حتى آخر 200 حجز).</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-bold">{value}</span>
    </div>
  );
}